/**
 * @description
 * Invoice Management page for comprehensive invoice handling.
 * Provides filtering, search, bulk actions, and manual invoice creation.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 * @see admin/src/frontend/pages/InvoiceManagement.test.tsx
 */

import { Plus, HelpCircle, ExternalLink } from 'lucide-react';
import { useInvoiceData } from './InvoiceManagement/hooks/useInvoiceData';
import { InvoiceFilters } from './InvoiceManagement/InvoiceFilters';
import { InvoiceTable } from './InvoiceManagement/InvoiceTable';
import { ManualInvoiceModal } from './InvoiceManagement/ManualInvoiceModal.tsx';

/**
 * Invoice Management page component
 */
export function InvoiceManagement() {
  const {
    clients,
    subscriptions,
    loading,
    searchTerm,
    statusFilter,
    typeFilter,
    clientFilter,
    showCreateModal,
    selectedInvoices,
    filteredInvoices,
    setSearchTerm,
    setStatusFilter,
    setTypeFilter,
    setClientFilter,
    setShowCreateModal,
    handleBulkAction,
    handleSelectInvoice,
    handleSelectAll,
    loadData,
  } = useInvoiceData();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Invoice Management</h1>
            <p className="text-muted-foreground">
              Manage all invoices, create manual invoices, and track payment status
            </p>
          </div>
          <a
            href="/docs/stripe/DOCUMENTATION.md"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            title="Open Stripe Billing Documentation"
          >
            <HelpCircle className="h-4 w-4" />
            <span>Help</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <InvoiceFilters
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        typeFilter={typeFilter}
        clientFilter={clientFilter}
        clients={clients}
        selectedInvoices={selectedInvoices}
        onSearchChange={setSearchTerm}
        onStatusFilterChange={setStatusFilter}
        onTypeFilterChange={setTypeFilter}
        onClientFilterChange={setClientFilter}
        onBulkAction={handleBulkAction}
      />

      {/* Invoice Table */}
      <InvoiceTable
        invoices={filteredInvoices}
        selectedInvoices={selectedInvoices}
        onSelectInvoice={handleSelectInvoice}
        onSelectAll={handleSelectAll}
      />

      {/* Manual Invoice Creation Modal */}
      {showCreateModal && (
        <ManualInvoiceModal
          clients={clients}
          subscriptions={subscriptions}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */